import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import {
  ClipboardList, ShoppingCart, PackageOpen, Search, X, ChevronDown, ChevronRight,
  RefreshCcw, Filter,
} from "lucide-react";
import MultiSelect from "../components/MultiSelect";

/**
 * Shared log view for either "sale" or "purchase".
 * Props: kind = "sale" | "purchase"
 */
export default function TransactionLog({ kind }) {
  const isSale = kind === "sale";
  const endpoint = isSale ? "/logs/sales" : "/logs/purchases";
  const partyLabel = isSale ? "Customer" : "Vendor";
  const partyType = isSale ? "customer" : "vendor";
  const Icon = isSale ? ShoppingCart : PackageOpen;
  const testidRoot = isSale ? "sale-log" : "purchase-log";

  const [godowns, setGodowns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [parties, setParties] = useState([]);

  const [brand, setBrand] = useState([]);
  const [size, setSize] = useState([]);
  const [godown, setGodown] = useState([]);
  const [partyId, setPartyId] = useState([]);
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(todayISO());
  const [search, setSearch] = useState("");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [g, b, s, p] = await Promise.all([
          api.get("/godowns"),
          api.get("/brands"),
          api.get("/sizes"),
          api.get(`/parties?type=${partyType}`),
        ]);
        setGodowns((g.data || []).filter((x) => x.kind !== "bullet"));
        setBrands(b.data || []);
        setSizes(s.data || []);
        setParties(p.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [partyType]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, {
        params: {
          brand: brand.length ? brand.join(",") : undefined,
          size: size.length ? size.join(",") : undefined,
          godown: godown.length ? godown.join(",") : undefined,
          party_id: partyId.length ? partyId.join(",") : undefined,
          date_from: from || undefined,
          date_to: to || undefined,
        },
      });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [brand, size, godown, partyId, from, to]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data?.rows || [];
    return (data?.rows || []).filter((r) =>
      (r.party_name || "").toLowerCase().includes(q) ||
      (r.challan_no || "").toLowerCase().includes(q) ||
      (r.bill_no || "").toLowerCase().includes(q) ||
      (r.vehicle_no || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const summary = data?.summary || {};
  const clearFilters = () => { setBrand([]); setSize([]); setGodown([]); setPartyId([]); setSearch(""); };
  const hasFilter = brand.length || size.length || godown.length || partyId.length || search;

  return (
    <div className="space-y-6" data-testid={`${testidRoot}-page`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon size={20} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">{isSale ? "Sale Logs" : "Purchase Logs"}</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Every {kind} · filter by brand, size, {partyLabel.toLowerCase()}, godown & date
        </span>
        <button onClick={load} className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black" data-testid={`${testidRoot}-refresh`}>
          <RefreshCcw size={13} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card-flat p-5 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          <div>
            <label className="small-label block mb-1.5">Brand</label>
            <MultiSelect
              options={brands.map((b) => ({ value: b.name, label: b.name }))}
              values={brand} onChange={setBrand}
              placeholder="All brands" testid={`${testidRoot}-brand`} />
          </div>
          <div>
            <label className="small-label block mb-1.5">Size</label>
            <MultiSelect
              options={sizes.map((s) => ({ value: s.name, label: s.name }))}
              values={size} onChange={setSize}
              placeholder="All sizes" testid={`${testidRoot}-size`} />
          </div>
          <div>
            <label className="small-label block mb-1.5">Godown</label>
            <MultiSelect
              options={godowns.map((g) => ({ value: g.name, label: g.name }))}
              values={godown} onChange={setGodown}
              placeholder="All godowns" testid={`${testidRoot}-godown`} />
          </div>
          <div>
            <label className="small-label block mb-1.5">{partyLabel}</label>
            <MultiSelect
              options={parties.map((p) => ({ value: p.id, label: p.name }))}
              values={partyId} onChange={setPartyId}
              placeholder={`All ${partyLabel.toLowerCase()}s`}
              testid={`${testidRoot}-party`} />
          </div>
          <div>
            <label className="small-label block mb-1.5">From</label>
            <input type="date" className="input-lg h-9 text-sm" value={from} onChange={(e) => setFrom(e.target.value)}
              data-testid={`${testidRoot}-from`} />
          </div>
          <div>
            <label className="small-label block mb-1.5">To</label>
            <input type="date" className="input-lg h-9 text-sm" value={to} onChange={(e) => setTo(e.target.value)}
              data-testid={`${testidRoot}-to`} />
          </div>
          <div>
            <label className="small-label block mb-1.5">Search</label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="input-lg h-9 text-sm pl-8" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="party / challan / bill / vehicle"
                data-testid={`${testidRoot}-search`} />
            </div>
          </div>
        </div>
        {hasFilter && (
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-gray-500">
            <Filter size={11} />
            <span>Active filters:</span>
            {brand.length > 0 && <button onClick={() => setBrand([])} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200"><span>Brand: {brand.length === 1 ? brand[0] : `${brand.length} brands`}</span><X size={10} /></button>}
            {size.length > 0 && <button onClick={() => setSize([])} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200"><span>Size: {size.length === 1 ? size[0] : `${size.length} sizes`}</span><X size={10} /></button>}
            {godown.length > 0 && <button onClick={() => setGodown([])} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200"><span>Godown: {godown.length === 1 ? godown[0] : `${godown.length} godowns`}</span><X size={10} /></button>}
            {partyId.length > 0 && <button onClick={() => setPartyId([])} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200"><span>{partyLabel}: {partyId.length === 1 ? (parties.find((p) => p.id === partyId[0])?.name || "?") : `${partyId.length} ${partyLabel.toLowerCase()}s`}</span><X size={10} /></button>}
            {search && <button onClick={() => setSearch("")} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200"><span>Search: {search}</span><X size={10} /></button>}
            <button onClick={clearFilters} className="text-[11px] font-semibold text-[#B91C1C] hover:underline" data-testid={`${testidRoot}-clear`}>Clear all</button>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-flat p-4 bg-black text-white border-black">
          <div className="small-label text-white/70">Total {isSale ? "sales" : "purchases"}</div>
          <div className="data-num text-2xl mt-1" data-testid={`${testidRoot}-kpi-amount`}>₹ {formatMoney(summary.amount || 0)}</div>
          <div className="text-[10px] text-white/70 mt-0.5">{summary.count || 0} {kind}s</div>
        </div>
        <div className="card-flat p-4">
          <div className="small-label text-gray-500">Filled cylinders</div>
          <div className="data-num text-2xl mt-1" data-testid={`${testidRoot}-kpi-filled`}>{summary.filled_qty || 0}</div>
        </div>
        <div className="card-flat p-4 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309]">MT cylinders</div>
          <div className="data-num text-2xl mt-1 text-[#B45309]" data-testid={`${testidRoot}-kpi-mt`}>{summary.mt_qty || 0}</div>
        </div>
        <div className="card-flat p-4">
          <div className="small-label text-gray-500">Total KG</div>
          <div className="data-num text-2xl mt-1">{formatMoney(summary.kg || 0)}</div>
        </div>
        <div className="card-flat p-4">
          <div className="small-label text-gray-500">Avg ₹ / kg</div>
          <div className="data-num text-2xl mt-1">{summary.rate_per_kg ? `₹ ${summary.rate_per_kg}` : "—"}</div>
        </div>
      </div>

      {/* Table */}
      <div className="card-flat">
        <div className="table-wrap">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Challan</th>
                <th className="text-left">{partyLabel}</th>
                <th className="text-left">Godown</th>
                <th className="num">Filled</th>
                <th className="num">MT</th>
                <th className="num">KG</th>
                <th className="num">Amount ₹</th>
                <th className="text-left">Vehicle</th>
                <th className="text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="10" className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="10" className="py-8 text-center text-sm text-gray-400">No {kind}s in this window.</td></tr>
              )}
              {!loading && rows.map((r) => {
                const opened = !!expanded[r.txn_id];
                return (
                  <React.Fragment key={r.txn_id}>
                    <tr data-testid={`${testidRoot}-row-${r.txn_id}`}>
                      <td className="data-num text-xs whitespace-nowrap">{r.date}</td>
                      <td>
                        <div className="data-num font-bold">{r.challan_no || "—"}</div>
                        {r.bill_no && <div className="text-[10px] text-gray-500 data-num">Bill: {r.bill_no}</div>}
                      </td>
                      <td>
                        {r.party_id ? (
                          <Link to={`/parties/${r.party_id}`} className="font-semibold hover:underline text-sm">{r.party_name}</Link>
                        ) : <span className="text-gray-400">—</span>}
                        {r.party_phone && <div className="text-[10px] text-gray-500 data-num">{r.party_phone}</div>}
                      </td>
                      <td className="text-xs">{r.godown || "—"}</td>
                      <td className="num data-num font-bold text-[#15803D]">{r.filled_qty || ""}</td>
                      <td className="num data-num font-bold text-[#B45309]">{r.mt_qty || ""}</td>
                      <td className="num data-num text-xs text-gray-500">{r.kg ? formatMoney(r.kg) : ""}</td>
                      <td className="num data-num font-bold">{r.amount ? formatMoney(r.amount) : ""}</td>
                      <td className="text-xs text-gray-500">
                        {r.vehicle_no || ""}
                        {r.driver_name && <div>{r.driver_name}</div>}
                      </td>
                      <td className="text-right">
                        <button onClick={() => setExpanded((s) => ({ ...s, [r.txn_id]: !s[r.txn_id] }))}
                          className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-0.5"
                          data-testid={`${testidRoot}-expand-${r.txn_id}`}>
                          {opened ? <ChevronDown size={12} /> : <ChevronRight size={12} />} items
                        </button>
                      </td>
                    </tr>
                    {opened && (
                      <tr>
                        <td colSpan="10" className="bg-gray-50 border-b border-gray-100">
                          <div className="p-3">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left font-semibold pb-1">Brand</th>
                                  <th className="text-left font-semibold pb-1">Size</th>
                                  <th className="num font-semibold pb-1">Filled</th>
                                  <th className="num font-semibold pb-1">MT</th>
                                  <th className="num font-semibold pb-1">Rate ₹</th>
                                  <th className="num font-semibold pb-1">Line ₹</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(r.items || []).map((it, i) => (
                                  <tr key={i}>
                                    <td>{it.brand}</td>
                                    <td>{it.size}</td>
                                    <td className="num">{it.filled_qty || ""}</td>
                                    <td className="num">{it.mt_qty || ""}</td>
                                    <td className="num">{it.rate ? it.rate.toFixed(2) : ""}</td>
                                    <td className="num">{it.amount ? formatMoney(it.amount) : ""}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-500 leading-relaxed">
        <ClipboardList size={12} className="inline mr-1" />
        When a brand or size filter is set, only line items matching that filter contribute to KPI totals and are shown in the expanded item block.
      </p>
    </div>
  );
}
